import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  employee_job_role_id: '',
  name: '',
  description: '',
  is_active: true,
  sort_order: 0
};

export default function EmployeeTitles() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('users_modify');
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [jobRoleFilter, setJobRoleFilter] = useState('');

  const loadRoles = useCallback(async () => {
    try {
      const r = await api.get('/contacts/employee-job-roles');
      setRoles(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load job roles');
      setRoles([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/employee-titles');
      setRows(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load employee levels');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      employee_job_role_id: row.employee_job_role_id || '',
      name: row.name || '',
      description: row.description || '',
      is_active: row.is_active !== false,
      sort_order: Number(row.sort_order || 0)
    });
    setOpen(true);
  };

  const save = async () => {
    if (!String(form.employee_job_role_id || '').trim()) {
      toast.error('Employee Job Role is required');
      return;
    }
    if (!String(form.name || '').trim()) {
      toast.error('Level name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_job_role_id: form.employee_job_role_id,
        name: String(form.name).trim(),
        description: form.description != null ? String(form.description) : null,
        is_active: form.is_active,
        sort_order: Number(form.sort_order || 0)
      };
      if (editing?.id) {
        await api.put(`/admin/employee-titles/${editing.id}`, payload);
        toast.success('Employee level updated');
      } else {
        await api.post('/admin/employee-titles', payload);
        toast.success('Employee level created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save employee level');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/employee-titles/${row.id}`);
      toast.success('Employee level deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete employee level');
    }
  };

  const filteredRows = useMemo(() => {
    if (!jobRoleFilter) return rows;
    return rows.filter((r) => String(r.employee_job_role_id) === String(jobRoleFilter));
  }, [rows, jobRoleFilter]);

  const groupedByRole = useMemo(() => {
    const map = new Map();
    for (const row of filteredRows) {
      const roleId = row.employee_job_role_id || 'unknown';
      const roleName = row.employeeJobRole?.name || '—';
      if (!map.has(roleId)) {
        map.set(roleId, { roleId, roleName, items: [] });
      }
      map.get(roleId).items.push(row);
    }
    const groups = Array.from(map.values());
    groups.forEach((g) => {
      g.items.sort((a, b) => {
        const so = Number(a.sort_order || 0) - Number(b.sort_order || 0);
        if (so !== 0) return so;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    });
    groups.sort((a, b) => a.roleName.localeCompare(b.roleName));
    return groups;
  }, [filteredRows]);

  const colCount = canModify ? 6 : 5;

  const renderGroupedRows = () => {
    if (filteredRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={colCount}>
            <Typography color="text.secondary">
              {rows.length === 0 ? 'No employee levels found.' : 'No levels for the selected job role.'}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }
    let serial = 0;
    return groupedByRole.flatMap((group) => [
      <TableRow key={`grp-${group.roleId}`} sx={{ bgcolor: 'action.selected' }}>
        <TableCell colSpan={colCount} sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {group.roleName}
          </Typography>
        </TableCell>
      </TableRow>,
      ...group.items.map((row) => {
        serial += 1;
        return (
          <TableRow key={row.id} hover>
            <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {serial}
            </TableCell>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.description || '—'}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={row.is_active ? 'Active' : 'Inactive'}
                color={row.is_active ? 'success' : 'default'}
                variant={row.is_active ? 'filled' : 'outlined'}
              />
            </TableCell>
            <TableCell align="right">{Number(row.sort_order || 0)}</TableCell>
            {canModify && (
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => remove(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        );
      })
    ]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Employee Levels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define internal levels per Employee Job Role. Contacts pick a level after choosing a role. The title offered
            to the employee is configured separately under Employee Offered Title.
          </Typography>
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Add Employee Level
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 2, maxWidth: 360 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="employee-levels-job-role-filter">Job Role</InputLabel>
          <Select
            labelId="employee-levels-job-role-filter"
            label="Job Role"
            value={jobRoleFilter}
            onChange={(e) => setJobRoleFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All job roles</em>
            </MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell width={56} align="center">
                    S.No.
                  </TableCell>
                  <TableCell>Level (internal)</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Sort</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>{renderGroupedRows()}</TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Employee Level' : 'Add Employee Level'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Employee Job Role</InputLabel>
            <Select
              label="Employee Job Role"
              value={form.employee_job_role_id || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, employee_job_role_id: e.target.value }))}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Level name (internal catalog)"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Sort Order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Active
            </Typography>
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
